import type { Chart } from 'klinecharts'
import { IndicatorLegend, type ActiveIndicator } from './IndicatorLegend'
import { useIndicatorValues } from '../hooks/useIndicatorValues'
import type { EventBus } from '../core/EventBus'

export interface IndicatorLegendLayerProps {
  chart: Chart | null
  eventBus: EventBus | null
  indicators: ActiveIndicator[]
  /** `settings.hideIndicatorLabels` — indicators keep drawing, only the
   *  floating legend rows go away. */
  hideLabels: boolean
  /** Top offset for the main-pane legend: below the OHLC row by default,
   *  lifted to the pane top when the OHLC row is hidden, pushed down a row
   *  when the sort-value badge occupies the slot under the OHLC header. */
  mainTop: number
  left: number
  mainCollapsed: boolean
  onToggleMainCollapse: () => void
  subPaneIds: string[]
  subPaneTops: Record<string, number>
  subPaneHeights: Record<string, number | undefined>
  onToggleVisibility: (key: string) => void
  onRemove: (key: string) => void
  onUpdateParams: (key: string, newParams: unknown[]) => void
  onOpenSettings?: (key: string, anchor?: { x: number; y: number }) => void
  onMovePane?: (key: string) => void
  onLocate?: (key: string) => void
  onToggleFocus?: (key: string) => void
}

/**
 * Owns the crosshair/tick-driven legend readouts and renders every
 * IndicatorLegend (main pane + one per sub-pane).
 *
 * This is deliberately a LEAF between EdgeChart and the legends:
 * `useIndicatorValues` re-computes on every crosshair bar crossing and every
 * realtime tick, and when that state lived at the top of EdgeChart each
 * update re-rendered the entire chart chrome (Toolbar, DrawingBar, menus…).
 * Housed here, a value change re-renders only the legend rows. Do not lift
 * the hook back up — IndicatorLegendLayer.unit.test.tsx pins the host
 * render-isolation contract.
 */
export function IndicatorLegendLayer({
  chart,
  eventBus,
  indicators,
  hideLabels,
  mainTop,
  left,
  mainCollapsed,
  onToggleMainCollapse,
  subPaneIds,
  subPaneTops,
  subPaneHeights,
  onToggleVisibility,
  onRemove,
  onUpdateParams,
  onOpenSettings,
  onMovePane,
  onLocate,
  onToggleFocus,
}: IndicatorLegendLayerProps) {
  const indicatorValues = useIndicatorValues(chart, indicators, eventBus)

  if (hideLabels) return null

  return (
    <>
      {/* Indicator legend overlay — main pane. */}
      <IndicatorLegend
        indicators={indicators}
        indicatorValues={indicatorValues}
        onToggleVisibility={onToggleVisibility}
        onRemove={onRemove}
        onUpdateParams={onUpdateParams}
        onOpenSettings={onOpenSettings}
        onMovePane={onMovePane}
        onLocate={onLocate}
        onToggleFocus={onToggleFocus}
        paneId="candle_pane"
        top={mainTop}
        left={left}
        collapsed={mainCollapsed}
        onToggleCollapse={onToggleMainCollapse}
      />
      {/* One legend per sub-pane, anchored inside its pane (RSI/MACD/VOL).
          Rendered only once we know the pane's top, so it never flashes over
          the candle-pane OHLC header. */}
      {subPaneIds.map((pid) =>
        subPaneTops[pid] != null ? (
          <IndicatorLegend
            key={pid}
            indicators={indicators}
            indicatorValues={indicatorValues}
            onToggleVisibility={onToggleVisibility}
            onRemove={onRemove}
            onUpdateParams={onUpdateParams}
            onOpenSettings={onOpenSettings}
            onMovePane={onMovePane}
            onLocate={onLocate}
            onToggleFocus={onToggleFocus}
            paneId={pid}
            top={subPaneTops[pid] + 4}
            left={left}
            paneHeight={subPaneHeights[pid]}
          />
        ) : null,
      )}
    </>
  )
}
